import React, { useCallback, memo } from "react";
import MuiRadio from "@material-ui/core/Radio";
import MuiCheckbox from "@material-ui/core/Checkbox";
import MuiSwitch from "@material-ui/core/Switch";
import MuiRadioGroup from "@material-ui/core/RadioGroup";
import MuiFormGroup from "@material-ui/core/FormGroup";
import MuiToggleButton from "@material-ui/lab/ToggleButton";
import MuiToggleButtonGroup from "@material-ui/lab/ToggleButtonGroup";
import MuiFormControl, {
	FormControlProps as MuiFormControlProps
} from "@material-ui/core/FormControl";
import MuiFormLabel from "@material-ui/core/FormLabel";
import MuiFormControlLabel, {
	FormControlLabelProps as MuiFormControlLabelProps
} from "@material-ui/core/FormControlLabel";
import MuiFormHelperText from "@material-ui/core/FormHelperText";
import { RadioGroupProps as FormikRadioGroupProps } from "formik-material-ui";
import { Overwrite, Omit, HasItems, BaseFieldProps } from "./types";
import {
	identityRecordOfFieldEditorOptions,
	getFieldError,
	defaultGetItemLabel,
	defaultGetItemValue
} from "./utils";
import { makeStyles, Theme, ThemeProvider } from "@material-ui/core/styles";
import AddIcon from "@material-ui/icons/Add";
import ClearIcon from "@material-ui/icons/Clear";
import merge from "lodash/merge";
import clsx from "clsx";
import { Grid, Typography } from "@material-ui/core";

export interface RadioGroupOptions<T> extends HasItems<T> {}

const variants = [
	"standard",
	"switch",
	"button",
	"toggleButton",
	"toggleButtonNoIcons"
] as const;
const colors = ["primary", "secondary", "default"] as const;

export interface RadioGroupProps<T>
	extends Overwrite<
			FormikRadioGroupProps,
			Omit<MuiFormControlProps, "variant" | "color">
		>,
		Overwrite<BaseFieldProps, HasItems<T>> {
	variant?: typeof variants[number];
	color?: typeof colors[number];
	customOnChange?: (
		selected: string | number | boolean | string[] | number[]
	) => void;
}

const useStyles = makeStyles(
	(theme: Theme) => ({
		buttonGroup: {
			display: "flex",
			flexWrap: "wrap",
			marginTop: 8,
			marginBottom: 8
		},
		toggleButtonGroup: {
			flexGrow: 1,
			flexShrink: 0
		},
		toggleButtonSwitch: {
			color: theme.palette.primary.main,
			backgroundColor: "#fff",
			marginTop: 12,
			fontSize: 15
		},
		formGroup: {
			padding: "10px 0"
		},
		radioControlLabel: {
			padding: "9px 0"
		}
	}),
	{ name: "DploySelectionField" }
);

const toggleButtonSwitchTheme = (theme: Theme) => {
	const overrides = {
		...theme,
		overrides: {
			...theme.overrides,
			MuiToggleButton: {
				root: {
					"&:not(:first-child)": {
						border: `1px solid ${theme.palette.grey[700]}`,
						borderLeft: `1px solid ${theme.palette.grey[700]}`
					}
				}
			}
		}
	};
	return merge({}, theme, overrides) as Theme;
};

export function SelectionField<T extends any>(props: RadioGroupProps<T>) {
	const {
		field,
		form,
		helperText,
		disabled,
		options,
		errorDisplay,
		customOnChange,
		...rest
	} = props;
	const { name, value } = field;
	const { errors, touched, isSubmitting, setFieldValue } = form;

	const onChange = useCallback(
		(selected: string | number | boolean | string[] | number[]) => {
			if (customOnChange) customOnChange(selected);
			setFieldValue(name, selected);
		},
		[setFieldValue, customOnChange, name]
	);

	return (
		<DploySelectionField
			{...rest}
			{...getFieldError(errors, touched, field.name, errorDisplay)}
			name={name}
			value={value}
			disabled={isSubmitting || disabled}
			onChange={onChange}
		/>
	);
}

export interface DploySelectionFieldProps<T>
	extends Omit<MuiFormControlProps, "variant" | "onChange" | "color">,
		Overwrite<BaseFieldProps, HasItems<T>> {
	variant?: typeof variants[number];
	color?: typeof colors[number];
	onChange: (value?: any) => void;
	name?: string;
	value: any;
}

function DploySelectionFieldInternal<T extends any>({
	onChange,
	value,
	label,
	helperText,
	disabled,
	error,
	items = [],
	getItemLabel = defaultGetItemLabel,
	getItemValue = defaultGetItemValue,
	valueKey,
	labelKey,
	getItemSuggestions,
	onSelectItem,
	fullWidth,
	classes,
	variant,
	multiple,
	searchable,
	clearable,
	manual,
	pending,
	emptyEqualsZero,
	formatString,
	horizontal: row,
	color,
	italicText,
	boldText,
	modalText,
	textAlign,
	...props
}: DploySelectionFieldProps<T>) {
	const onButtonChange = useCallback(
		(_, selected: string | number | boolean) =>
			onChange?.(selected == null && !clearable ? value : selected),
		[onChange, value, clearable]
	);

	const onMultipleChange = useCallback(
		(selected: string | number | boolean) => {
			let list: any[] = [];
			if (!Array.isArray(value)) list = selected ? [selected] : [];
			else
				list = value?.includes(selected)
					? value?.filter(v => v !== selected)
					: value
					? [...value, selected]
					: [selected];
			if (onChange) onChange(list);
		},
		[onChange, value]
	);

	const selectionClasses = useStyles(props);

	let formGroup: React.ReactNode;

	switch (variant) {
		case "button": {
			formGroup = (
				<MuiToggleButtonGroup
					className={selectionClasses.buttonGroup}
					value={value}
					exclusive={!multiple}
					onChange={onButtonChange}
					color={color ?? "standard"}
					aria-disabled={disabled}
				>
					{items.map(item => {
						const itemValue = getItemValue(item);
						const itemLabel = getItemLabel(item).split("|")[0];

						return (
							<MuiToggleButton
								disabled={disabled}
								data-option-label={itemLabel}
								data-option-value={itemValue}
								key={String(itemLabel)}
								className={selectionClasses.toggleButtonGroup}
								value={itemValue}
							>
								{itemLabel}
							</MuiToggleButton>
						);
					})}
				</MuiToggleButtonGroup>
			);
			break;
		}
		case "toggleButtonNoIcons":
		case "toggleButton": {
			const notSelected = items.find(
				i => String(getItemValue(i)) !== String(value)
			);
			formGroup = (
				<ThemeProvider theme={toggleButtonSwitchTheme}>
					<MuiToggleButton
						disabled={disabled}
						selected={Boolean(value)}
						onChange={() => {
							onChange(getItemValue(notSelected as T));
						}}
						value={value}
						className={selectionClasses.toggleButtonSwitch}
					>
						{variant === "toggleButtonNoIcons" ? null : value ? (
							<ClearIcon />
						) : (
							<AddIcon />
						)}
						{notSelected && getItemLabel(notSelected)}
					</MuiToggleButton>
				</ThemeProvider>
			);
			break;
		}
		default: {
			const Control =
				variant === "switch" ? MuiSwitch : multiple ? MuiCheckbox : MuiRadio;
			const Group = multiple ? MuiFormGroup : MuiRadioGroup;

			formGroup = (
				<Group row={row} {...props} className={selectionClasses.formGroup}>
					{items.map((item, idx) => {
						const itemValue = getItemValue(item);
						const itemLabel = getItemLabel(item);

						return (
							<Grid key={itemValue?.toString() ?? idx.toString()} item xs>
								<FormControlLabel
									data-option-label={itemLabel}
									data-option-value={itemValue}
									key={String(
										typeof itemLabel === "object" ? itemValue : itemLabel
									)}
									onChange={multiple ? onMultipleChange : onChange}
									value={itemValue}
									disabled={false}
									label={
										<Typography
											className={clsx({
												[selectionClasses.radioControlLabel]:
													variant !== "switch" && !multiple
											})}
										>
											{itemLabel}
										</Typography>
									}
									control={
										<Control
											checked={
												multiple
													? value?.includes(itemValue) ?? false
													: value === itemValue
											}
											color={color}
										/>
									}
								/>
							</Grid>
						);
					})}
				</Group>
			);
		}
	}

	const labelStyle = merge(
		{},
		boldText ? { fontWeight: "bold" as const } : {},
		italicText ? { fontStyle: "italic" as const } : {}
	);
	return (
		<MuiFormControl
			variant="standard"
			component={"fieldset" as "div"}
			error={error}
			disabled={disabled}
			fullWidth={fullWidth}
			classes={classes}
		>
			<MuiFormLabel component={"legend" as "label"} style={labelStyle}>
				{label}
			</MuiFormLabel>
			{formGroup}
			{helperText && <MuiFormHelperText>{helperText}</MuiFormHelperText>}
		</MuiFormControl>
	);
}

DploySelectionFieldInternal.displayName = "DploySelectionField";

export const DploySelectionField = memo(DploySelectionFieldInternal);

type FormControlLabelProps<TValue> = Overwrite<
	MuiFormControlLabelProps,
	{
		value: TValue;
		onChange: (value: TValue) => void;
	}
>;

const FormControlLabel = <TValue extends any>(
	props: FormControlLabelProps<TValue>
) => {
	const { onChange: propsOnChange, value } = props;

	const onChange = useCallback(
		() => propsOnChange(value),
		[value, propsOnChange]
	);

	return (
		<MuiFormControlLabel
			{...props}
			value={String(props.value)}
			onChange={onChange}
		/>
	);
};

export const EditorSelectionFields = identityRecordOfFieldEditorOptions({
	SelectionField: {
		editableOptions: {
			variant: variants,
			color: colors,
			horizontal: true,
			multiple: true,
			optionPairs: true
		}
	}
});
